import { useEffect, useMemo, useState } from "react";
import { Loader2, Mail, Check } from "lucide-react";
import { toast } from "react-toastify";
import { useStateValue } from "../../context/StateProvider";
import {
  loadListingRequestRecipients,
  saveListingRequestRecipients,
} from "../../utils/notificationSettings";

function setsEqual(a, b) {
  if (a.size !== b.size) return false;
  for (const x of a) if (!b.has(x)) return false;
  return true;
}

export default function NotificationsTab() {
  const [{ user, users }] = useStateValue();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [seller, setSeller] = useState(new Set());
  const [buyer, setBuyer] = useState(new Set());
  const [initSeller, setInitSeller] = useState(new Set());
  const [initBuyer, setInitBuyer] = useState(new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const lists = await loadListingRequestRecipients();
      if (cancelled) return;
      const s = new Set(lists.seller || []);
      const b = new Set(lists.buyer || []);
      setSeller(s);
      setBuyer(b);
      setInitSeller(s);
      setInitBuyer(b);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const eligibleUsers = useMemo(
    () =>
      (users || [])
        .filter((u) => u?.type !== 1 && u?.type !== 2)
        .filter((u) => ["Admin", "Supervisor", "Employee"].includes(u?.role))
        .sort((a, b) =>
          `${a.firstname || ""} ${a.lastname || ""}`.localeCompare(
            `${b.firstname || ""} ${b.lastname || ""}`,
            undefined,
            { sensitivity: "base" },
          ),
        ),
    [users],
  );

  const dirty = !setsEqual(seller, initSeller) || !setsEqual(buyer, initBuyer);

  const toggle = (which, id) => {
    const setter = which === "seller" ? setSeller : setBuyer;
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveListingRequestRecipients(
        { seller: [...seller], buyer: [...buyer] },
        user?.id,
      );
      setInitSeller(new Set(seller));
      setInitBuyer(new Set(buyer));
      toast.success("Notification recipients saved.");
    } catch (err) {
      console.error(err);
      toast.error("Save failed. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const renderColumn = (label, which, set) => (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden flex flex-col">
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
        <h4 className="text-sm font-semibold text-slate-900">{label}</h4>
        <p className="text-[11px] text-slate-500 mt-0.5">
          {set.size} recipient{set.size === 1 ? "" : "s"}
        </p>
      </div>
      <div className="p-3 max-h-[400px] overflow-y-auto">
        {eligibleUsers.length === 0 ? (
          <p className="text-sm text-slate-500 py-4 text-center">
            No team members available.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {eligibleUsers.map((u) => {
              const isSelected = set.has(u.id);
              const roleLabel =
                u.role === "Admin"
                  ? "Team Lead"
                  : u.role === "Supervisor"
                    ? "Client Care"
                    : "Agent";
              return (
                <li key={u.id}>
                  <button
                    type="button"
                    onClick={() => toggle(which, u.id)}
                    className={`w-full text-left flex items-center gap-2.5 px-2.5 py-2 rounded-md border transition ${
                      isSelected
                        ? "border-themeColor bg-slate-50"
                        : "border-slate-200 hover:border-slate-300 bg-white"
                    }`}
                  >
                    <span
                      className={`size-4 rounded border flex items-center justify-center flex-shrink-0 ${
                        isSelected
                          ? "bg-themeColor border-themeColor text-white"
                          : "border-slate-300 bg-white"
                      }`}
                    >
                      {isSelected && <Check size={12} />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-medium text-slate-900 truncate">
                        {u.firstname} {u.lastname}
                      </div>
                      <div className="text-[11px] text-slate-500 truncate">
                        {roleLabel}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
        <Mail size={18} className="text-linkColor" />
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-slate-900">
            Request &amp; Turn-In Notifications
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Choose who gets an email when a new Listing Request (seller side) or
            Buyer Request is submitted, or when a sale is turned in on that side.
            Lists are independent — pick different people for each.
          </p>
        </div>
      </div>

      <div className="p-5">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-6">
            <Loader2 size={14} className="animate-spin" />
            Loading…
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderColumn("Seller Side", "seller", seller)}
            {renderColumn("Buyer Side", "buyer", buyer)}
          </div>
        )}
      </div>

      <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={!dirty || saving}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-md bg-slate-800 hover:bg-slate-900 disabled:opacity-50 text-white"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
